import { httpClient } from "./client";
import { InvalidApiResponseError } from "./errors";
import type { MembershipApi } from "./interfaces";
import type {
  VenuePlanResponse,
  SubscribeRequest,
  SubscriptionResponse,
  VisitBalanceResponse,
  EntitlementUsageResponse,
} from "../types/generated";
import type { UUID } from "../types";

const PENDING_STATES = new Set([
  "activation_pending",
  "cancellation_pending",
  "reactivation_pending",
  "refund_pending",
]);
const CUSTOMER_ACTIONS = new Set(["complete_payment", "cancel", "reactivate"]);

function subscriptionResponse(
  value: unknown,
  endpoint: string,
): SubscriptionResponse {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new InvalidApiResponseError(endpoint);
  const row = value as Record<string, unknown>;
  const text = (key: string) =>
    typeof row[key] === "string" && (row[key] as string).trim().length > 0;
  if (
    !text("subscription_id") ||
    !text("user_id") ||
    !text("facility_id") ||
    !text("plan_id") ||
    !text("status") ||
    !text("created_at") ||
    !(
      row.stripe_connect_account_id === null ||
      text("stripe_connect_account_id")
    ) ||
    !(
      row.pending_state === null ||
      (typeof row.pending_state === "string" &&
        PENDING_STATES.has(row.pending_state))
    ) ||
    !Array.isArray(row.available_actions) ||
    !row.available_actions.every(
      (action) => typeof action === "string" && CUSTOMER_ACTIONS.has(action),
    )
  )
    throw new InvalidApiResponseError(endpoint);
  return value as SubscriptionResponse;
}

async function readSubscription(
  response: Promise<unknown>,
  endpoint: string,
): Promise<SubscriptionResponse> {
  return subscriptionResponse(await response, endpoint);
}

/**
 * Membership journey client (#265/#266, enriched #1707) — the single
 * authoritative surface for the player membership lifecycle: discover a
 * facility's plans, subscribe (real recurring Stripe charge on that facility's
 * connected account), read/cancel/reactivate a subscription, and read visit
 * balances (visits_card passes).
 *
 * The retired `transactionApi.subscribe` sent no payment method, so it could
 * never bill; this `subscribe` carries `stripe_payment_method_id` in the body.
 * See .claude/rules/clean-migration.md (one path, no dual-path shim).
 *
 * Visit balances are READ-only here — minting happens server-side inside
 * subscribe() / the invoice.paid webhook (the old public mint route was removed
 * in #1707 as a self-mint hole).
 *
 * `getEntitlementUsage` (Slice 6.2) reads a member's included-sessions usage
 * this billing period for one (subscription, service) pair — also read-only;
 * consumption happens server-side (MembershipService.consume_entitlement,
 * wired into class enrollment).
 */
export const membershipApi: MembershipApi = {
  listFacilityPlans: (facilityId: UUID) =>
    httpClient.get<VenuePlanResponse[]>(
      `/facilities/${facilityId}/public/plans`,
    ),

  listSubscriptions: async () => {
    const rows = await httpClient.get<unknown>("/memberships/");
    if (!Array.isArray(rows))
      throw new InvalidApiResponseError("/memberships/");
    return rows.map((row) => subscriptionResponse(row, "/memberships/"));
  },

  getSubscription: (subscriptionId: UUID, signal?: AbortSignal) =>
    readSubscription(
      httpClient.get<unknown>(`/memberships/${subscriptionId}`, { signal }),
      `/memberships/${subscriptionId}`,
    ),

  subscribe: (data: SubscribeRequest) =>
    readSubscription(
      httpClient.post<unknown>("/memberships/subscribe", data),
      "/memberships/subscribe",
    ),

  cancelSubscription: (subscriptionId: UUID) =>
    httpClient.delete<void>(`/memberships/${subscriptionId}`),

  reactivateSubscription: (subscriptionId: UUID) =>
    readSubscription(
      httpClient.post<unknown>(`/memberships/${subscriptionId}/reactivate`),
      `/memberships/${subscriptionId}/reactivate`,
    ),

  confirmSubscription: (subscriptionId: UUID) =>
    readSubscription(
      httpClient.post<unknown>(`/memberships/${subscriptionId}/confirm`),
      `/memberships/${subscriptionId}/confirm`,
    ),

  listVisitBalances: () =>
    httpClient.get<VisitBalanceResponse[]>("/memberships/visit-balances"),

  getVisitBalance: (subscriptionId: UUID, signal?: AbortSignal) =>
    httpClient.get<VisitBalanceResponse>(
      `/memberships/visit-balances/${subscriptionId}`,
      { signal },
    ),

  getEntitlementUsage: (subscriptionId: UUID, service: string) =>
    httpClient.get<EntitlementUsageResponse>(
      `/memberships/${subscriptionId}/entitlements/${service}/usage`,
    ),
};
